import { Box } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'

import { markAsWixStudioAccount } from '../../../api/StudioWorkspaceService/markAsWixStudioAccount'
import { usePageLogger } from '../../../bi/mfi'
import { useBrowserTabTitle } from '../../../hooks/useBrowserTabTitle'
import { Button } from '../../reusable/Button'
import { HeadlessTemplate } from '../../reusable/HeadlessTemplate'
import { dataHooks } from './HeadlessTwoRadioInputs.dataHooks'
import s from './HeadlessTwoRadioInputs.scss'
import { Question } from './Question'

export type Content = {
  backButton: string
  nextButton: string
  skipButton: string
  stepText: string
  browserTabTitle: string
  title: string
  subtitle: string
  question1: ChoiceQuestionContentComponent<{ title: string }, { label: string; showQuestion2WhenSelected: boolean }>
  question2: ChoiceQuestionContentComponent<{ title: string }, { label: string }>
}

export const HeadlessTwoRadioInputs: FC = () => {
  const [isPageLoading] = usePageLoading()
  const next = useNext()

  const [question1Answer, setQuestion1Answer] = useState<string>()
  const [question2Answer, setQuestion2Answer] = useState<string>()

  const { content } = useContent<Content>()
  const { questionKey: question1Key } = content.question1
  const { questionKey: question2Key } = content.question2

  useBrowserTabTitle(content.browserTabTitle)

  const pageLogger = usePageLogger()

  useEffect(() => {
    // request was to mark headless as wix studio account in the beginning of the headless flow
    // this template will be rendered as first one for flow https://wix.com/intro/headless
    void markAsWixStudioAccount()
  }, [])

  const handleSkip = () => {
    next({
      [question1Key]: { skipped: true },
      [question2Key]: { skipped: true },
    })
  }

  const handleNext = () => {
    void pageLogger.answer({
      answer: {
        [question1Key]: question1Answer,
        [question2Key]: question2Answer ?? '',
      },
    })

    next({
      [question1Key]: { values: [question1Answer!] },
      [question2Key]: { values: question2Answer ? [question2Answer] : [] },
    })
  }

  const question1SelectedOption = content.question1.options.find(({ optionKey }) => optionKey === question1Answer)
  const showQuestion2 = question1SelectedOption?.showQuestion2WhenSelected
  const isNextEnabled = Boolean(showQuestion2 ? question1Answer && question2Answer : question1Answer)

  return (
    <HeadlessTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      stepText={content.stepText}
      title={content.title}
      subtitle={content.subtitle}
      backButton={content.backButton}
      skipButton={content.skipButton}
      onSkipClick={handleSkip}
    >
      <Box className={s.mainContainer}>
        <Box className={s.questionContainer}>
          <Question
            {...content.question1}
            selectedOptionKey={question1Answer}
            onChange={setQuestion1Answer}
            pageLogger={pageLogger}
            dataHook={dataHooks.question1()}
          />
          {showQuestion2 && (
            <Box marginTop="SP5" width="100%">
              <Question
                {...content.question2}
                selectedOptionKey={question2Answer}
                onChange={setQuestion2Answer}
                pageLogger={pageLogger}
                dataHook={dataHooks.question2()}
              />
            </Box>
          )}
        </Box>
        <Box className={s.nextButton}>
          <Button
            fullWidth
            skin="standard"
            label={content.nextButton}
            onClick={handleNext}
            disabled={!isNextEnabled}
            isLoading={isPageLoading}
            dataHook={dataHooks.nextButton()}
          />
        </Box>
      </Box>
    </HeadlessTemplate>
  )
}
